import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import { INK, FG_2, FG_3, LINE, FONTS } from '../sections/theme.js';
import { IMS_CERTIFICATION_URL } from '../../constants.js';

const CARDS = [
    { key: 'ob', img: '/images/home/std-1edtech.jpg', bg: 'rgba(81,36,166,0.12)', href: IMS_CERTIFICATION_URL },
    {
        key: 'vc',
        img: '/images/home/std-w3c.jpg',
        bg: 'rgba(73,222,255,0.22)',
        href: 'https://www.w3.org/TR/vc-data-model-2.0/',
    },
    {
        key: 'elm',
        img: '/images/home/std-europass.jpg',
        bg: 'rgba(84,140,255,0.16)',
        href: 'https://europass.europa.eu/en/european-digital-credentials',
    },
    { key: 'iso', img: '/images/home/std-iso-soc2.jpg', bg: 'rgba(255,204,0,0.22)', href: 'https://trust.pok.tech/' },
];

export default function StandardsCards() {
    const { t } = useTranslation();

    return (
        <Box component="section" sx={{ background: '#fff', py: { xs: 6, md: 9 } }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <HeroBadge label={t('home.standards.tag')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', md: '2.125rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            letterSpacing: '-0.01em',
                            color: INK,
                            mb: 1.5,
                        }}
                    >
                        {t('home.standards.title')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.0625rem',
                            fontWeight: 400,
                            color: FG_2,
                            lineHeight: 1.55,
                            maxWidth: 640,
                            mx: 'auto',
                        }}
                    >
                        {t('home.standards.desc')}
                    </Typography>
                </Box>

                <Grid container spacing={2.75}>
                    {CARDS.map((card) => (
                        <Grid key={card.key} size={{ xs: 12, sm: 6 }}>
                            <Box
                                component="a"
                                href={card.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    background: '#fff',
                                    border: `1px solid ${LINE}`,
                                    borderRadius: '20px',
                                    overflow: 'hidden',
                                    textDecoration: 'none',
                                    transition: 'transform .18s ease, box-shadow .18s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 22px 46px -22px rgba(20,11,61,.22)',
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={card.img}
                                    alt={t(`home.standards.items.${card.key}.title`)}
                                    loading="lazy"
                                    sx={{
                                        width: '100%',
                                        height: 200,
                                        objectFit: 'cover',
                                        background: card.bg,
                                        display: 'block',
                                    }}
                                />
                                <Box sx={{ p: 3 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONTS.mono,
                                            fontSize: '0.6rem',
                                            fontWeight: 500,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: FG_3,
                                            mb: 1,
                                        }}
                                    >
                                        {t(`home.standardsCats.${card.key}`)}
                                    </Typography>
                                    <Typography
                                        component="h3"
                                        sx={{
                                            fontSize: '1.03rem',
                                            fontWeight: 700,
                                            lineHeight: 1.25,
                                            color: INK,
                                            mb: 1.1,
                                        }}
                                    >
                                        {t(`home.standards.items.${card.key}.title`)}
                                    </Typography>
                                    <Typography
                                        sx={{ fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.55, color: FG_2 }}
                                    >
                                        {t(`home.standards.items.${card.key}.desc`)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}
